import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import useRecommendation from 'components/shared/useEffect/useRecommendation'
import BackdropModal from 'components/portal/backdrop-modal'
import RelatedProductLayer from 'components/shared/product/product-related/related-product-layer'
import { PDP_WISHLIST_REF_TYPE } from 'components/pdp/const'
import { BUTTON_HTML_TYPE } from 'constants/button'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import ResponsiveImage from 'components/images/responsive-image'
import Typo from 'constants/typography'
import Router from 'next/router'
import styles from './shop-the-look.scss'

const ShopTheLook = ({
  t,
  isPhone,
  productId,
  isHiddenShopTheLook,
  shopLookCtaEventTracking,
}) => {
  const [isOpenShopTheLook, setIsOpenShopTheLook] = useState(false)
  const [recommendedProducts] = useRecommendation(productId)
  const recommendedProductsCoverImages =
    recommendedProducts?.map(
      (product) => product?.images?.full_cover_image_path,
    ) ?? []

  const maxProductsImages = recommendedProductsCoverImages.slice(0, 3)
  const totalProducts = recommendedProductsCoverImages?.length
  const totalProductsText = hasLocizeTranslation(
    t,
    'NUMBER_ITEMS',
    `(%s items)`,
  ).replace('%s', totalProducts)

  const handleClickShopTheLook = () => {
    setIsOpenShopTheLook(true)
    if (shopLookCtaEventTracking) {
      shopLookCtaEventTracking()
    }
  }

  useEffect(() => {
    const handleRouteChange = () => {
      setIsOpenShopTheLook(false) // Close modal on route change
    }

    Router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [])

  if (!recommendedProducts) return null

  if (isPhone) {
    return (
      <React.Fragment>
        <style jsx>{styles}</style>
        <div
          role="presentation"
          data-cy="pdp__shop-the-look-cta"
          className="pdp__shop-the-look-mobile"
          onClick={handleClickShopTheLook}
          onKeyDown={handleClickShopTheLook}
        >
          <div
            className={clsx(
              'pdp__shop-the-look-mobile__wrapper',
              `shop-the-look-fade-${isHiddenShopTheLook ? 'out' : 'in'}`,
            )}
          >
            <div className="pdp__shop-the-look-mobile__img-stack">
              {maxProductsImages.map((image, index) => (
                <ResponsiveImage
                  key={`${image}${index}`}
                  className="pdp__shop-the-look-mobile__img"
                  src={image}
                  imgRatio={1.33}
                  lazy
                  lazyThreshold={0.1}
                />
              ))}
            </div>
            <div className="pdp__shop-the-look-mobile__content">
              <span
                className={clsx(
                  'pdp__shop-the-look-mobile__content__title',
                  Typo.caption,
                )}
              >
                {hasLocizeTranslation(
                  t,
                  'SHOP_THE_LOOK_TITLE',
                  'Complete The Look',
                )}
              </span>
              <span
                className={clsx(
                  'pdp__shop-the-look-mobile__content__count',
                  Typo.price2,
                )}
              >
                {totalProductsText}
              </span>
            </div>
          </div>
        </div>
        <BackdropModal
          zIndex={2000}
          freezeBody={isPhone}
          isOpen={isOpenShopTheLook}
          onBackdropClick={() => {
            setIsOpenShopTheLook(false)
          }}
        >
          <div
            id="Shop_the_look"
            data-cy="pdp__shop-the-look-modal"
            className="pdp__shop-the-look-modal"
          >
            <RelatedProductLayer
              showTwoPerRow
              productId={productId}
              products={recommendedProducts}
              title={hasLocizeTranslation(
                t,
                'SHOP_THE_LOOK_TITLE',
                'Complete The Look',
              )}
              refType={PDP_WISHLIST_REF_TYPE.related}
              elementID="Shop_the_look"
              displayHoverInteractions
              displayQuickAddToBag
            />
          </div>
        </BackdropModal>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <div
        role="presentation"
        data-cy="pdp__shop-the-look-cta"
        className="pdp__shop-the-look-desktop"
        onClick={handleClickShopTheLook}
        onKeyDown={handleClickShopTheLook}
      >
        <div className="pdp__shop-the-look-desktop__wrapper">
          <div className="pdp__shop-the-look-desktop__img-stack">
            {maxProductsImages.map((image, index) => (
              <ResponsiveImage
                key={`${image}${index}`}
                className="pdp__shop-the-look-desktop__img"
                src={image}
                imgRatio={1.33}
                lazy
                lazyThreshold={0.1}
              />
            ))}
          </div>
          <div className="pdp__shop-the-look-desktop__content">
            <span
              className={clsx(
                'pdp__shop-the-look-desktop__content__title',
                Typo.caption,
              )}
            >
              {hasLocizeTranslation(
                t,
                'SHOP_THE_LOOK_TITLE',
                'Complete The Look',
              )}
            </span>
            <span
              className={clsx(
                'pdp__shop-the-look-desktop__content__count',
                Typo.price2,
              )}
            >
              {totalProductsText}
            </span>
          </div>
        </div>
      </div>
      <BackdropModal
        zIndex={2000}
        freezeBody={isPhone}
        isOpen={isOpenShopTheLook}
        onBackdropClick={() => {
          setIsOpenShopTheLook(false)
        }}
      >
        <div
          id="Shop_the_look"
          data-cy="pdp__shop-the-look-modal"
          className="pdp__shop-the-look-modal"
        >
          <button
            data-cy="pdp__shop-the-look-modal__close-ctn"
            className="pdp__shop-the-look-modal__close-ctn"
            type={BUTTON_HTML_TYPE.button}
            onClick={() => {
              setIsOpenShopTheLook(false)
            }}
          >
            <Icon
              alt="close dialog"
              src={ICONS.close}
              size={ICON_SIZE.medium}
            />
          </button>
          <RelatedProductLayer
            showTwoPerRow
            productId={productId}
            products={recommendedProducts}
            title={hasLocizeTranslation(
              t,
              'SHOP_THE_LOOK_TITLE',
              'Complete The Look',
            )}
            refType={PDP_WISHLIST_REF_TYPE.related}
            elementID="Shop_the_look"
            displayHoverInteractions
            displayQuickAddToBag
          />
        </div>
      </BackdropModal>
    </React.Fragment>
  )
}

ShopTheLook.defaultProps = {
  isPhone: false,
  isHiddenShopTheLook: true,
  shopLookCtaEventTracking: null,
}

ShopTheLook.propTypes = {
  isPhone: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isHiddenShopTheLook: PropTypes.bool,
  shopLookCtaEventTracking: PropTypes.func,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
}

export default withI18next()(ShopTheLook)
